import React, { useCallback, useEffect } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Logger from 'js-logger';
import _get from 'lodash/get';
import { hooks, VideoStream } from '@lifesize/clients.sdk';
// @ts-ignore
import { media } from '@lifesize/nucleus';
import { makeStyles, Box, Theme } from '@material-ui/core';
import { ReactComponent as BackIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-back.svg';
import colors from '../../styles/variables/baseColors.module.scss'; // bridge from scss variables to js
import ImagePicker from 'components/ImagePicker/ImagePicker';
import VbbDisclaimer from 'components/VbbSettings/VbbDisclaimer';
import { bgSelectionType } from 'reducers/vbbSettingsSlice';
import { vbbManager } from 'utils/vbbManager';
import { isCallConnected } from 'utils/call-utils';
import { defaultVideoAspect } from 'utils/videoResolution';
import { getConstraints } from 'utils/deviceUtils';

const messages = defineMessages({
  btnBack: { id: 'components.vbbSettings.btnBack', defaultMessage: 'Back' }
});

interface Props {
  intl: { formatMessage: Function };
  isJoinFlowCall?: boolean;
  isStartCallFlow?: boolean;
  onToggleVbbSettings: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const VbbSettings = (props: Props) => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const { intl, onToggleVbbSettings } = props;
  const { enabled: vbbEnabled, selection: bgSelection } = useSelector(state => _get(state, 'app.vbbSettings'));
  const { localVideoMuted } = hooks.useMedia();
  const localPrimaryStream = hooks.useLocalPrimaryStream();
  const mediaSettings = useSelector(state => _get(state, 'app.mediaSettings'));

  const activeCall = hooks.useCallState();
  const isInCall = isCallConnected(activeCall);

  const onSelectBg = useCallback(
    (newBgSelection: bgSelectionType, saveAsDefault: boolean) => {
      if (newBgSelection.id === bgSelection.id) {
        Logger.warn('Chose same background as existing setting; not calling vbbManager.', bgSelection.id);
        return;
      }

      vbbManager.chooseBackgroundSetting(dispatch, mediaSettings, localVideoMuted, vbbEnabled, newBgSelection, saveAsDefault);
    },
    [dispatch, localVideoMuted, vbbEnabled, bgSelection, mediaSettings]
  );

  const modalCloseVbbCleanup = useCallback(() => {
      // We can stop camera if we're not in a call
      if (!isInCall && !props.isJoinFlowCall) {
        // If VBB is not enabled, this is a no-op, so no harm calling it.
        vbbManager.pauseVbb(mediaSettings, false).catch(e => Logger.warn('Error pausing VBB', e));
      }
    },
    [isInCall, mediaSettings, props.isJoinFlowCall]
  );

  useEffect(() => {
    // componentDidMount
    const acquire = async () => {
      try {
        // SDK /should/ respect video mute, if it were already set, per streamManager.acquireWebcam()
        if (!media.getPrimaryStream()) {
          await media.acquireWebcam(constraints);
        }
      } catch (ex) {
        Logger.warn('Could not capture webcam', ex);
      }
    };
    const constraints = getConstraints(mediaSettings);
    const backgroundIsSelected = bgSelection.id !== 'none';

    // If we're out-of-call and we have a non-"none" background, then init vbb if needed
    if (!isInCall && backgroundIsSelected) {
      Logger.debug('Initializing VBB on settings component mount', bgSelection);
      vbbManager.enableVbb(dispatch, mediaSettings, bgSelection);
    } else if (!isInCall && !props.isJoinFlowCall) {
      // OOC w/ No bg selected, ensure we acquire a primary stream
      // this is the same as what VideoSettings does
      Logger.info('attempting to capture stream', constraints);
      acquire();
    }

    return function componentWillUnmount() {
      try {
        if (!isInCall && !props.isJoinFlowCall) {
          // Cleanup only if out-of-call so that we don't bork existing stream
          media.stopWebcam();
          modalCloseVbbCleanup();
        }
      }
      catch (error) {
        Logger.warn('Error: media.stopWebcam() failed', { error });
      }
    };
    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.container}>
      <section className={classes.header}>
        <a onClick={onToggleVbbSettings} className={classes.backBtn}>
          <BackIcon />
          <label>{intl.formatMessage(messages.btnBack)}</label>
        </a>
      </section>
      <section className={classes.preview}>
        <Box className={classes.videoPreview}>
          <VideoStream elementId="vbbPreview" className={classes.videoMirror} muted={true} mediaStream={localPrimaryStream.stream} />
        </Box>
        <Box className={classes.previewInfo}>
          <VbbDisclaimer />
        </Box>
      </section>
      <section className={classes.picker}>
        <ImagePicker onSelect={onSelectBg} isJoinFlowCall={props.isJoinFlowCall} isStartCallFlow={props.isStartCallFlow} />
      </section>
    </Box>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 700,
    overflow: 'hidden',
    width: '100%'
  },
  header: {
    display: 'flex',
    flex: '0 0 auto',
    marginBottom: theme.spacing(3),
  },
  backBtn: {
    color: colors.chambray,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 12,

    '& svg': {
      height: 16,
      marginRight: 8,
      width: 'auto'
    },
    '& label': {
      cursor: 'inherit',
      lineHeight: 1,
      marginTop: '0.25em'
    }
  },
  preview: {
    display: 'flex',
    flex: '0 0 auto',
    width: '100%'
  },
  videoPreview: {
    flex: '0 0 auto',
    paddingTop: `calc(50% * ${defaultVideoAspect})`, // magic to maintain aspect ratio
    position: 'relative',
    width: '50%',
    '& video': {
      bottom: 0,
      height: '100%',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%'
    }
  },
  videoMirror: {
    transform: 'rotateY(180deg)'
  },
  previewInfo: {
    flex: '0 0 auto',
    paddingLeft: theme.spacing(2),
    width: '50%'
  },
  picker: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    marginTop: theme.spacing(2)
  }
}));

export default injectIntl(VbbSettings);
